import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { ADMIN_SIGNUP_PATH } from 'adminSignUp/index'
import { Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { SignupValues } from 'core/application/account/common'
import getDiscountCodeInfo from 'core/application/account/discountCodeInfo'
import signupController from 'core/application/account/signup'
import { ProductIds } from 'core/application/commons/config'
import localStorage from 'core/application/commons/localStorage'

import Tooltip from 'common/components/dataDisplay/Tooltip'
import PasswordInput from 'common/components/dataEntry/formik/FormikPasswordField'
import Input from 'common/components/dataEntry/formik/FormikStyledInputField'
import Alert from 'common/components/feedback/Alert'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'
import Content from 'common/components/layout/Content'
import Footer from 'common/components/layout/Footer'
import Layout from 'common/components/layout/Layout'
import PageHeader from 'common/components/onBoarding/PageHeader'
import { generalPasswordRegx } from 'common/components/utils/Regx'

import useOverrideProduct from 'common/hooks/useOverrideProduct'
import useUrlQueryParam from 'common/hooks/useUrlQueryParam'

import { commandRequest, queryRequest } from 'common/RequestUtils'

import AccountHeader from '../AccountHeader'
import RightSection from '../RightSection'

import '../AccountStyles.less'

export const SIGNUP_PATH = '/signup'

const formValidator = (password: string, translate: any) =>
  Yup.object({
    firstName: Yup.string().trim().required(translate('general.required')),
    lastName: Yup.string().trim().required(translate('general.required')),
    email: Yup.string().email(translate('admin-signup.signup.errors.email')).required(translate('general.required')),
    password: Yup.string()
      .required(translate('general.required'))
      .min(8, translate('admin-signup.signup.errors.password-regex'))
      .matches(generalPasswordRegx, translate('admin-signup.signup.errors.password-regex')),
    passwordConfirmation: Yup.string()
      .oneOf([password], translate('admin-signup.signup.errors.confirm-password'))
      .required(translate('general.required')),
  })

const initialValues: SignupValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  isSelfServe: false,
}
const Signup = () => {
  const [password, setPassword] = useState<string>('')
  const [submiting, setSubmiting] = useState(false)

  const [errors, setErrors] = useState<string[]>([])
  let discountCode = useUrlQueryParam('discountCode')
  let planCode = useUrlQueryParam('planCode')
  const subscriptionPackageCode = useUrlQueryParam('package')
  const productId = useUrlQueryParam('productId') ?? ProductIds.GroupHraId
  const source = useUrlQueryParam('source')
  const history = useHistory()

  const currentProduct = useOverrideProduct(productId ?? ProductIds.GroupHraId)
  const [discountCodeIsValid, setDiscountCodeIsValid] = useState<boolean>(false)
  const { t: translate } = useTranslation()
  const loginPath = `/login?productId=${productId}`

  const getDiscountCodeInformation = async () => {
    const result = await queryRequest(() => getDiscountCodeInfo(discountCode))
    if (!result) {
      setDiscountCodeIsValid(false)
      notification.open({
        message: 'Discount code is not valid',
        type: 'error',
      })
    } else {
      setDiscountCodeIsValid(true)
      notification.open({
        message: 'Discount code is valid',
        type: 'success',
      })
    }
  }

  useEffect(() => {
    if (discountCode) {
      getDiscountCodeInformation()
    }
  }, [discountCode])

  useEffect(() => {
    localStorage.setPlanCode(planCode ?? '')
    localStorage.setSubscriptionPackageCode(subscriptionPackageCode ?? '')
  }, [planCode])

  const onSubmit = async (values: SignupValues) => {
    setSubmiting(true)

    let accountValues: SignupValues = {
      ...values,
      discount: discountCodeIsValid ? discountCode : null,
      planCode: localStorage.getPlanCode(),
      subscriptionPackageCode: localStorage.getSubscriptionPackageCode(),
      productId: productId ? parseInt(productId as string) : ProductIds.GroupHraId,
      isSelfServe: source === 'selfserve',
    }

    const res = await commandRequest(() => signupController(accountValues))

    if (!res.errors) {
      setSubmiting(false)
      localStorage.setToken(res.token)
      window.location.href = ADMIN_SIGNUP_PATH
    } else {
      setSubmiting(false)
      setErrors(res.errors)
    }
  }

  const errorDescription = (
    <div>
      <p>{translate('admin-signup.signup.errors.general')}</p>
      <ul>
        {errors.map((e) => (
          <li>{e}</li>
        ))}
      </ul>
    </div>
  )

  const getCreateAccountButton = (formik: any): ReactNode => {
    const createAccountButton = (
      <Button
        icon="fa fa-arrow-right"
        iconPosition="right"
        htmlType="submit"
        type="primary"
        className="button__large"
        disabled={!formValidator(password, translate).isValidSync(formik.values)}
        loading={submiting}
      >
        {translate('admin-signup.signup.create-button')}
      </Button>
    )

    const missingInput = !formValidator(password, translate).isValidSync(formik.values)

    return missingInput ? (
      <Tooltip title={translate('admin-signup.signup.create-button-tooltip')} position="left">
        {createAccountButton}
      </Tooltip>
    ) : (
      createAccountButton
    )
  }
  return (
    <Layout className="signup-responsive-layout t-signup">
      <Content>
        <Formik
          enableReinitialize={true}
          validationSchema={formValidator(password, translate)}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit} className={'t-signup-form'} autoComplete="off">
              <Row>
                <Col xs={23} sm={19} md={19} lg={16}>
                  <Row justify="center">
                    <Col span={24} sm={{ offset: 2 }} xs={{ offset: 1 }} md={{ offset: 5 }} lg={{ offset: 10 }}>
                      <div className="pt-16 mb-64">
                        <AccountHeader />
                      </div>
                    </Col>
                  </Row>
                  <Row justify="center" align="middle">
                    <Col xs={23} sm={23} md={15} lg={10}>
                      <Row align="middle" justify="center">
                        <Col xs={24} md={24}>
                          <PageHeader title={translate('admin-signup.signup.title')} content="" />
                        </Col>
                        <Row>
                          <Col xs={24} md={24}>
                            {errors.length > 0 && (
                              <Alert
                                className={'t-feedback-error'}
                                type="error"
                                message={translate('admin-signup.signup.errors.create-account-fail')}
                                description={errorDescription}
                                showIcon
                              />
                            )}
                          </Col>
                        </Row>
                      </Row>
                      <Row className="form__row" gutter={10}>
                        <Col xs={24} md={12} className="pt-24">
                          <Input label={translate('general.first-name')} name="firstName"></Input>
                        </Col>

                        <Col xs={24} md={12} className="pt-24">
                          <Input label={translate('general.last-name')} name="lastName"></Input>
                        </Col>
                      </Row>
                      <Row className="form__row">
                        <Col xs={24} md={24}>
                          <Input
                            label={translate('general.email')}
                            name="email"
                            hint={translate('admin-signup.signup.email-hint')}
                          ></Input>
                        </Col>
                      </Row>
                      <Row className="form__row">
                        <Col xs={24} md={24}>
                          <PasswordInput
                            label={translate('general.password')}
                            name="password"
                            popover={
                              window.innerWidth > 767 ||
                              (formik.values.password !== '' &&
                                !formik.values.password.match(generalPasswordRegx) &&
                                true)
                            }
                            popoverPlacement={window.innerWidth < 767 && 'topRight'}
                            setPassword={(password: string) => {
                              setPassword(password)
                            }}
                          ></PasswordInput>
                        </Col>
                      </Row>
                      <Row className="form__row">
                        <Col xs={24} md={24}>
                          <PasswordInput
                            label={translate('general.confirm-password')}
                            name="passwordConfirmation"
                            setPassword={() => {}}
                          ></PasswordInput>
                        </Col>
                      </Row>
                      <Col xs={24} md={24} className="login--login-button pt-16 pb-16">
                        <Button
                          icon="fa fa-arrow-right"
                          iconPosition="right"
                          htmlType="submit"
                          type="primary"
                          className="button__large"
                          disabled={!formValidator(password, translate).isValidSync(formik.values)}
                          loading={submiting}
                        >
                          {translate('admin-signup.signup.create-button')}
                        </Button>
                      </Col>
                      <Row className="form__row" justify="end">
                        <Col>
                          <p>
                            Already have an account?{' '}
                            <Button type="link" className="signup--login p-0" onClick={() => history.push(loginPath)}>
                              {' '}
                              Log in
                            </Button>
                          </p>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
                <Col
                  xs={0}
                  lg={8}
                  xl={8}
                  className="account-right-section-signup account-right-section__fixed"
                  style={{ height: '100%' }}
                >
                  <RightSection
                    headerTitle={translate(`${currentProduct?.signUpTitle}`)}
                    headerItems={[
                      translate('admin-signup.signup.header-item1'),
                      translate('admin-signup.signup.header-item2'),
                      translate('admin-signup.signup.header-item3'),
                    ]}
                  />
                </Col>
              </Row>
            </form>
          )}
        </Formik>
      </Content>
      <Footer />
    </Layout>
  )
}

export default Signup
